import React, { useEffect, useRef, useState } from 'react';

import { Modal, showModal } from 'modal.react.js';
import { useDispatch, useSelector } from 'react-redux';

import FormSection from 'components/Layouts/Form/FormSection';
import LayoutWithFooter from 'components/Layouts/shared/LayoutWithFooter';
import { BuilderFooterButton } from 'components/Pages/Admin/PageTypes/shared/BuilderFooterButton';
import { ContentMissingWarningModal } from 'components/Pages/Admin/shared/Modals/ContentMissingWarningModal';
import { DuplicatedContentOnRowWarningModal } from 'components/Pages/Admin/shared/Modals/DuplicatedContentOnRowWarningModal';
import { DuplicatedContentWarningModal } from 'components/Pages/Admin/shared/Modals/DuplicatedContentWarningModal';
import FormActions from 'components/shared/FormAdapters/FormDefault/FormActions';
import area from 'components/shared/GlobalModalArea/area';
import CustomModal from 'components/shared/GlobalModalArea/Modal';
import { useScrollPageToTop } from 'hooks/useScrollPageToTop';
import { itinerariesItemDataSelector } from 'store/itinerariesItem/selectors';
import { updateItinerariesItemThunk } from 'store/itinerariesItem/thunks';
import { resetItineraryDetailsAction } from 'store/itineraryDetails';
import { getIsInitialized, getItineraryScheduleSelector } from 'store/itineraryDetails/selectors';
import { initializeSheduleThunk } from 'store/itineraryDetails/thunks';
import { transformFEScheduleToBEForm } from 'store/itineraryDetails/transform';
import {
  useItinerariesItemNextStepFormHandler,
  useItinerariesItemSaveHandler,
} from 'utils/itineraries/functions/builderUtils';

import ItinerariesItemActionButtons from '../../../ItinerariesItemBuilder/ActionButtons';
import { ItineraryBuilderSwitch } from '../ItineraryBuilderSwitch/ItineraryBuilderSwitch';
import Table from '../Table';
import { EDetailsMode } from '../types';
import {
  useIgnoreWarning,
  useUnsavedChangesWatcher,
  validateContentPresence,
  validateNoDuplicatedContentOnConsecutiveRows,
  validateNoDuplicatedContentOnSameRow,
} from './functions';
import s from './index.module.css';

function DetailsOriginalModeInner() {
  useScrollPageToTop();
  const dispatch = useDispatch();
  const schedule = useSelector(getItineraryScheduleSelector);
  const isInitialized = useSelector(getIsInitialized);
  const itinerary = useSelector(itinerariesItemDataSelector);
  const [isTableDisplayed, setIsTableDisplayed] = useState(true);
  const { onBeforeSave, onAfterSave: onAfterSaveUnsavedChangesWatcher, onAfterNext } = useUnsavedChangesWatcher();
  const goToNextStep = useItinerariesItemNextStepFormHandler({ onBeforeNext: onBeforeSave, onAfterNext });
  const shouldRegisterMentionRemovalRef = useRef(false);
  const detailsFormSectionRef = useRef(null);
  const [checkIsWarningIgnored, ignoreWarning] = useIgnoreWarning();
  const [isSaving, setIsSaving] = React.useState(false);

  // remount the table for applying the remote state from redux after save
  const handleRemountTable = () => {
    setIsTableDisplayed(false);
    setIsTableDisplayed(true);
  };
  const onAfterSave = () => {
    dispatch(initializeSheduleThunk());
    handleRemountTable();
    onAfterSaveUnsavedChangesWatcher();
  };
  const save = useItinerariesItemSaveHandler({ onBeforeSave, onAfterSave });
  const [submitting, setSubmitting] = useState(false);
  const handleSubmit = (handler) => async () => {
    try {
      shouldRegisterMentionRemovalRef.current = false;
      setSubmitting(true);

      const save = async () => await handler(transformFEScheduleToBEForm(schedule, itinerary));
      if (!checkIsWarningIgnored('ContentPresence') && !validateContentPresence(schedule)) {
        const res = await showModal(area, (res, rej) => <ContentMissingWarningModal onClose={rej} onSave={res} />);
        if (res.isIgnoreForProposal) ignoreWarning('ContentPresence');
      }
      if (
        !checkIsWarningIgnored('DuplicatedContentOnConsecutiveRows') &&
        !validateNoDuplicatedContentOnConsecutiveRows(schedule)
      ) {
        const res = await showModal(area, (res, rej) => <DuplicatedContentWarningModal onClose={rej} onSave={res} />);
        if (res.isIgnoreForProposal) ignoreWarning('DuplicatedContentOnConsecutiveRows');
      }
      if (!checkIsWarningIgnored('DuplicatedContentOnSameRow') && !validateNoDuplicatedContentOnSameRow(schedule)) {
        const res = await showModal(area, (res, rej) => (
          <DuplicatedContentOnRowWarningModal onClose={rej} onSave={res} />
        ));
        if (res.isIgnoreForProposal) ignoreWarning('DuplicatedContentOnSameRow');
      }

      await save();
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    dispatch(initializeSheduleThunk({ createItemIfEmpty: true }));

    return () => {
      shouldRegisterMentionRemovalRef.current = false;
      dispatch(resetItineraryDetailsAction());
    };
  }, [dispatch]);

  if (!isInitialized) {
    return null;
  }

  return (
    <LayoutWithFooter>
      {({ footerComponent: FooterComponent }) => (
        // div below is responsible for an accurate positioning of popover
        // really important to set position: relative
        <div ref={detailsFormSectionRef} style={{ position: 'relative' }}>
          <FormSection
            popupLineShift="-2rem"
            style={{ maxWidth: 850, margin: '0 auto', paddingLeft: 25, paddingRight: 25, background: 'transparent' }}
          >
            <div className={s.container}>
              {isTableDisplayed && (
                <Table
                  onTableMount={() => {
                    shouldRegisterMentionRemovalRef.current = true;
                  }}
                  shouldRegisterMentionRemovalRef={shouldRegisterMentionRemovalRef}
                  mountEl={detailsFormSectionRef.current}
                />
              )}

              <FooterComponent>
                <FormActions isRight>
                  <BuilderFooterButton
                    disabled={submitting || isSaving}
                    onClick={handleSubmit(goToNextStep)}
                    id="itineraryDetailsNextButton"
                  >
                    Next
                  </BuilderFooterButton>
                </FormActions>
              </FooterComponent>

              <ItinerariesItemActionButtons
                submitting={submitting}
                onSave={() => handleSubmit(save)()}
                onIsSavingChange={setIsSaving}
              />
            </div>
          </FormSection>
        </div>
      )}
    </LayoutWithFooter>
  );
}

function DetailOriginalMode() {
  const [showModal, setShowModal] = useState(true);
  const itinerary = useSelector(itinerariesItemDataSelector);

  const dispatch = useDispatch();

  const handleOption = (option) => {
    dispatch(updateItinerariesItemThunk(itinerary.id, { detailsMode: option, detailsModeManuallyUpdated: true }));
  };

  const handleClose = () => {
    handleOption(EDetailsMode.Original);
    setShowModal(false);
  };

  return (
    <>
      <DetailsOriginalModeInner />
      {itinerary.id && !itinerary.detailsModeManuallyUpdated && showModal && (
        <Modal area={area}>
          <CustomModal
            isOpen
            toClose={handleClose}
            closeButtonClassName={s.closeButton}
            contentClassName={s.wrapper}
            size="lmd"
          >
            <ItineraryBuilderSwitch onOptionClick={handleOption} />
          </CustomModal>
        </Modal>
      )}
    </>
  );
}

export default DetailOriginalMode;
